/**
 * This is the entry point for Feature Hub App integration
 */

import React from 'react';
import { FeatureAppDefinition, FeatureAppEnvironment } from '@feature-hub/core';
import { ReactFeatureApp } from '@feature-hub/react';
import {
  ContentContextProvider,
  FootnoteContextProvider,
  InViewContextProvider,
} from '@oneaudi/feature-app-utils';
import { UniversalEditorProvider } from '@oneaudi/falcon-tools';
import { AsyncStateHolder, Dependencies } from './FeatureAppTypes';
import AsyncFeatureApp from './FeatureApp';
import { createInitialState, deserializeState } from './utils/stateUtils';
import { ContextProvider } from './context';
import { mapHeadlessContent } from './utils/mapHeadlessContent';
import { getDesignLibrary } from '../alpha-helpers';

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, Dependencies> = {
  dependencies: {
    featureServices: {
      'audi-content-service': '^1.0.0',
      'locale-service': '^1.0.0',
      'dbad:audi-vue-formatter-service': '1.0.0',
      'audi-footnote-reference-service': '^3.0.0',
    },
    externals: {
      '@feature-hub/react': '^3.5.0',
      react: '^17.0.2 || ^18.2.0',
      'react-dom': '^17.0.2 || ^18.2.0',
      'styled-components': '^5.3.0',
      ...getDesignLibrary({ unifiedWebCommonVersion: '^1.13.0' }),
    },
  },

  optionalDependencies: {
    featureServices: {
      's2:logger': '^1.0.0',
      's2:async-ssr-manager': '^1.0.0',
      'audi-tracking-service': '^2.2.0',
      's2:serialized-state-manager': '^1.0.0',
      'audi-render-mode-service': '^1.0.0',
    },
  },

  create: ({ featureServices }: FeatureAppEnvironment<Dependencies, void>) => {
    const {
      'audi-tracking-service': trackingService,
      'audi-content-service': contentService,
      's2:logger': logger,
      'dbad:audi-vue-formatter-service': vueFormatterService,
      'locale-service': localeService,
      'audi-footnote-reference-service': footnoteReferenceServiceScopeManager,
      's2:async-ssr-manager': asyncSsrManager,
      's2:serialized-state-manager': serializedStateManager,
      'audi-render-mode-service': renderModeService,
    } = featureServices;

    const footnoteReferenceService =
      footnoteReferenceServiceScopeManager?.getDefaultScopeRefService();
    if (trackingService) {
      // set feature app name as early as possible
      trackingService.featureAppName = 'audi-feature-app-image-fade-over-scroll';
    }

    const initialContent = contentService?.getContent();
    logger?.info('Feature App created | Ifosm - initialContent: ', initialContent);
    const content = mapHeadlessContent(initialContent);
    logger?.info('Feature App created | Ifosm - content: ', content);

    let asyncStateHolder: AsyncStateHolder;
    if (asyncSsrManager) {
      // on the server
      asyncSsrManager.scheduleRerender(
        (async () => {
          if (content) {
            asyncStateHolder = await createInitialState(
              /* eslint-disable-next-line @typescript-eslint/no-unsafe-argument */
              content,
              vueFormatterService,
              localeService,
              logger,
            );
            serializedStateManager?.register(() => JSON.stringify(asyncStateHolder));
          }
        })(),
      );
    } else {
      // on the client
      const serializedState = serializedStateManager?.getSerializedState();

      if (serializedState) {
        asyncStateHolder = deserializeState(serializedState);
      } else {
        logger?.warn(
          `Serialized state not found!. Possible reasons: \n 1. Running app in dev mode using the "audi-static-demo-integrator" which does not support SSR \n 2. Running app in prod mode and SSR is broken`,
        );
        if (content) {
          asyncStateHolder = () =>
            /* eslint-disable-next-line @typescript-eslint/no-unsafe-argument */
            createInitialState(content, vueFormatterService, localeService, logger);
        }
      }
    }

    return {
      render: () => {
        return (
          <InViewContextProvider logger={logger}>
            <ContentContextProvider contentService={contentService}>
              <FootnoteContextProvider footnoteReferenceService={footnoteReferenceService}>
                <div data-fefa-custom-id={footnoteReferenceService.getConsumerId()}>
                  <ContextProvider featureServices={featureServices}>
                    <UniversalEditorProvider
                      contentService={contentService}
                      renderModeService={renderModeService}
                    >
                      <AsyncFeatureApp asyncStateHolder={asyncStateHolder} />
                    </UniversalEditorProvider>
                  </ContextProvider>
                </div>
              </FootnoteContextProvider>
            </ContentContextProvider>
          </InViewContextProvider>
        );
      },
    };
  },
};

export default featureAppDefinition;
